import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

// Actions
import {
  closeGenericModal,
  openGenericModal,
  openSmallModal,
  refreshGenericModal,
} from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Commons
import { Button, ListView } from '@commons/utils/styledLibraryComponents';
import { CATEGORY_TYPES_OBJECT } from '@commons/constants/categoryTypes';
import { ENTITY_UNITS } from '@commons/constants/units';
import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { isUserDeepsight, isUserManager } from '@commons/utils/roles';
import { sortArrayOfObjectsAlphabetically } from '@commons/utils/sorting';
import { STANDARD_LISTVIEW_PADDING } from '@commons/constants/listViewProps';
import { translateUnit } from '@commons/utils/translateUnit';
import EmptyState from '@commons/EmptyState';
import GeneralEmptyStateListView from '@commons/GeneralEmptyStateListView';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import paginationUtils from '@commons/utils/listViewPagination';

// Contexts
import { CentralKitchenRecipeContext } from '@context/CentralKitchenRecipeContext';
import { getListViewPersistentFilters, PersistentFilterTypes } from '@context/utils';
import { RecipeContext } from '@context/RecipeContext';

// Selectors
import { getClientInfo } from '@selectors/client';
import { getHasRecipeVariants } from '@selectors/featureProps';
import { getSalesPointStores } from '@selectors/stores';

// Commons utils
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';

// Services
import clientService from '@services/client';
import recipeService from '@services/recipe';

// Utils
import { CHOICES_DROPDOWN_ACTIVE } from '@admin/utils/DropdownItems';
import { getActions, getRowActions } from './utils/actions';
import { getAvailabilitiesModalConfig } from './utils/availabitiliesModalContent';
import { getColumns } from './utils/columns';
import { getDeleteRecipesModalParams } from './utils/modalConfigurations';
import { getIsInCentralKitchenOnlyPage } from '@admin/utils/centralKitchen';
import DeepsightFiltersButton from '@admin/components/FilterButton';

// Components
import { ListViewContainer } from './styledComponents';
import ExportRecipeByStoreProgressModal from './components/ExportRecipeByStoreProgressModal';
import ExportRecipeModal from './components/ExportRecipeModal';

// Utils (export by store)
import { getExportByStoreModalConfig } from './utils/exportByStoreModalConfig';
import { getSelectableGroupsFromStores } from './utils/storeGroups';

// Hooks
import { ENUM_QUERY_PARAMS, useListViewQueryParams } from '@hooks/useListViewQueryParams';
import { useCategoriesBatchUpdateModal } from '@hooks/useCategoriesBatchUpdateModal';

moment.locale('fr');

export const Recipes = (props) => {
  const {
    user,
    pageLoading,
    pageLoaded,
    showSuccessMessage,
    showErrorMessage,
    openGenericModal,
    openModalExportInfo,
    history,
    client: { clientId, hasMultipleChannels, hasMultipleBrands },
    match: { path },
    refreshGenericModal,
    closeGenericModal,
    clientBrands,
    hasVariants,
    activeStores,
  } = props;

  // Global states - const
  const userLanguageCode = get(user, 'lnkLanguageAccountrel.code', 'fr');
  const isInCentralKitchenOnlyPage = getIsInCentralKitchenOnlyPage();

  const contextHandler =
    useContext(isInCentralKitchenOnlyPage ? CentralKitchenRecipeContext : RecipeContext) || {};

  const {
    // Applied filters
    selectedStatus,
    setSelectedStatus,
    selectedBrands,
    setSelectedBrands,
    // Advanced filters
    advancedFilters,
    setAdvancedFilters,
    // ListView params
    customParams,
    setCustomParams,
    search,
  } = contextHandler;

  const listViewRef = useRef();
  const hasInitializedFromUrl = useRef(false);

  const [emptyState, setEmptyState] = useState(false);
  const persistentFilters = getListViewPersistentFilters(contextHandler);

  // ListView states - const
  const [columns, setColumns] = useState([]);
  const [actions, setActions] = useState([]);
  const [rowActions, setRowActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialFetch, setIsInitialFetch] = useState(true);

  // Filter states
  const [filters, setFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(false);
  const [columnsFilterList, setColumnsFilterList] = useState([]);

  // Data states
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [selectedAvailabilities, setSelectedAvailabilities] = useState({
    hasStock: true,
    hasLoss: true,
    hasTransfer: true,
  });
  const [totalRecipes, setTotalRecipes] = useState(0);
  const [categories, setCategories] = useState([]);

  // Pagination states
  const [listViewQueryParams, setListViewQueryParams] = useListViewQueryParams(false);
  const [paginationProps, setPaginationProps] = useState(null);

  // Export-by-store modal state
  const [isExportByStoreModalOpen, setIsExportByStoreModalOpen] = useState(false);
  const [exportByStoreGroups, setExportByStoreGroups] = useState([]);
  const [exportByStoreSelectedGroups, setExportByStoreSelectedGroups] = useState([]);
  const [exportByStoreStores, setExportByStoreStores] = useState([]);
  const [exportByStoreSelectedStores, setExportByStoreSelectedStores] = useState([]);

  // Stores eligible for the export-by-store feature: sales points only (no central kitchens),
  // normalized so `groups` is always an array (guards downstream group filtering)
  const salesPointStores = useMemo(
    () =>
      getSalesPointStores(activeStores || []).map((store) => ({
        ...store,
        groups: store.groups ?? [],
      })),
    [activeStores],
  );

  /**************
   * Use Effects
   **************/
  useEffect(() => {
    if (
      (!listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE] &&
        !listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY] &&
        !listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE] &&
        !listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE]) ||
      (hasMultipleBrands && !selectedBrands?.length)
    ) {
      return;
    }

    // Don't fetch if filters are being applied - the applyFilters useEffect will handle it
    if (applyFilters) {
      return;
    }

    const contextFiltersToUpdate = persistentFilters.filter(
      ({ filterType }) => filterType === PersistentFilterTypes.LIST_VIEW,
    );

    contextFiltersToUpdate.forEach(({ contextParam, updateFunction, queryParam }) => {
      if (
        (!!listViewQueryParams[queryParam] || listViewQueryParams[queryParam] === '') &&
        listViewQueryParams[queryParam] !== contextParam
      ) {
        updateFunction(listViewQueryParams[queryParam]);
      }
    });

    getRecipes();
  }, [
    listViewQueryParams[ENUM_QUERY_PARAMS.SEARCH],
    listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY],
    listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE],
    listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE],
    listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE],
    customParams,
    get(selectedStatus, 'filterValue', false),
    selectedBrands,
    categories,
  ]);

  // First render (not exactly), to fetch brands if multiple brands
  useEffect(() => {
    if (!clientId) {
      return;
    }

    getBrandsOfClient();
    getCategoriesAndSubCategories();

    setColumns(getColumns(hasMultipleBrands, hasVariants));
  }, [clientId, hasMultipleBrands, hasVariants]);

  useEffect(() => {
    if (!columns.length) {
      return;
    }

    updateColumnsFilterList(recipes, columns);
  }, [columns, categories]);

  useEffect(() => {
    setColumns(getColumns(hasMultipleBrands, hasVariants));
  }, [recipes]);

  useEffect(() => {
    if (!selectedAvailabilities) {
      return;
    }

    const params = getAvailabilitiesModalConfig({
      handleAvailabilitiesUpdate,
      selectedItems: selectedRecipes,
      selectedAvailabilities,
      setSelectedAvailabilities,
      handleAvailabilitiesModalCloseCleanUp,
    });

    refreshGenericModal(params);
  }, [selectedAvailabilities]);

  useEffect(() => {
    const filteringOnActive = get(selectedStatus, 'filterValue', true);

    setActions(
      getActions({
        selectedItems: selectedRecipes,
        createRecipe,
        duplicateRecipe,
        handleDeletionWarningGenericModal,
        exportRecipes: triggerExport,
        exportRecipesByStore: triggerExportByStore,
        filteringOnActive,
        openGenericModal,
        enableRecipe,
        user,
        disableRecipes,
        activateMultipleChannels,
        clientHasMultipleChannels: hasMultipleChannels,
        openAvailabilitiesUpdateModal,
        openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
        hasVariants,
        isInCentralKitchenOnlyPage,
      }),
    );

    setRowActions(
      getRowActions({
        duplicateRecipe,
        handleDeletionWarningGenericModal,
        filteringOnActive,
        openGenericModal,
        enableRecipe,
        user,
        disableRecipes,
        openAvailabilitiesUpdateModal,
        openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
      }),
    );
  }, [recipes, selectedRecipes, selectedStatus, selectedAvailabilities, isLoading]);

  useEffect(() => {
    if (isEmpty(contextHandler)) {
      return;
    }

    // Skip on initial mount if URL already has query params (from refresh or direct URL)
    // But allow context to sync to URL when coming back from detail page (no URL params)
    if (!hasInitializedFromUrl.current && listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY]) {
      hasInitializedFromUrl.current = true;
      return;
    }

    const listViewQueryParamsToUpdate = persistentFilters.filter(
      ({ filterType }) => filterType === PersistentFilterTypes.LIST_VIEW,
    );

    listViewQueryParamsToUpdate.forEach(({ contextParam, queryParam }) => {
      if (contextParam && contextParam.length && listViewQueryParams[queryParam] !== contextParam) {
        setListViewQueryParams[queryParam](contextParam);
      }
    });
  }, [
    /*
      We watch every variable instead of only contextHandler because it is refreshed at every change and we don't
      want to enter in this useEffect on every change, only when a state is changed
    */
    !!contextHandler &&
      (contextHandler.search ||
        contextHandler.orderBy ||
        contextHandler.orderType ||
        contextHandler.maxPerPage ||
        contextHandler.currentPage),
  ]);

  // Set pagination props like SupplierProducts
  useEffect(() => {
    setPaginationProps(
      paginationUtils.getPropsListView({
        listViewRef,
        listViewQueryParams,
        setListViewQueryParams,
        count: totalRecipes,
        onQueryParamsChange: () => true, // not used but necessary to adapt select MaxPage ListView
      }),
    );
  }, [totalRecipes]);

  // When selected groups change, recompute filtered stores and remove deselected stores
  useEffect(() => {
    if (!isExportByStoreModalOpen) {
      return;
    }

    const filteredStores = exportByStoreSelectedGroups.length
      ? getFilteredStoresWithSelectedGroups(exportByStoreSelectedGroups, salesPointStores)
      : salesPointStores;

    const filteredStoreIds = new Set(filteredStores.map(({ id }) => id));

    setExportByStoreStores(filteredStores);
    setExportByStoreSelectedStores((prev) => prev.filter(({ id }) => filteredStoreIds.has(id)));
  }, [exportByStoreSelectedGroups, isExportByStoreModalOpen, salesPointStores]);

  // Refresh the export-by-store selection modal whenever selection state changes
  useEffect(() => {
    if (!isExportByStoreModalOpen) {
      return;
    }

    refreshGenericModal(
      getExportByStoreModalConfig({
        groups: exportByStoreGroups,
        selectedGroups: exportByStoreSelectedGroups,
        setSelectedGroups: setExportByStoreSelectedGroups,
        stores: exportByStoreStores,
        selectedStores: exportByStoreSelectedStores,
        setSelectedStores: setExportByStoreSelectedStores,
        handleExport: handleExportByStore,
        handleCloseCleanUp: handleExportByStoreCloseCleanUp,
      }),
    );
  }, [
    isExportByStoreModalOpen,
    exportByStoreGroups,
    exportByStoreSelectedGroups,
    exportByStoreStores,
    exportByStoreSelectedStores,
    // handleExportByStore closes over selectedRecipes; re-run so the modal gets a fresh handler
    selectedRecipes,
  ]);

  const goToRecipeDetailsPage = (recipe) => {
    const path = getIsInCentralKitchenOnlyPage()
      ? `/admin/kitchen-products/recipes/${recipe.id}/details`
      : `/admin/products/recipes/${recipe.id}/details`;

    history.push(path);
  };

  // Apply filters
  useEffect(() => {
    if (!applyFilters || !listViewRef || !listViewRef.current) {
      return;
    }

    if ((!advancedFilters || !advancedFilters.length) && applyFilters) {
      listViewRef.current.resetPagination();

      // Trigger refresh of products when filters are reset
      setCustomParams({});
      setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](1);

      // Reset applyFilters flag
      setApplyFilters(false);

      return;
    }

    const params = advancedFilters.reduce(
      (result, { getQueryParamOperation, propertyKey, value }) => {
        result[propertyKey] = getQueryParamOperation(propertyKey, value);

        return result;
      },
      {},
    );

    // Trigger refresh of products when filters are applied
    setCustomParams(params);
    setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](1);

    listViewRef.current.resetPagination();

    // Reset applyFilters flag
    setApplyFilters(false);
  }, [advancedFilters, applyFilters]);

  /**************
   * Methods
   **************/
  const preprocessRecipes = (recipes) => {
    const formattedRecipes = recipes.map((recipe) => {
      recipe.unit = translateUnit(recipe.unit) || '';
      recipe.id = recipe.id || '';
      recipe.name = recipe.name || '';
      recipe.quantity = recipe.quantity || '';
      recipe.mappingsCount = recipe.mappingsCount || null;
      recipe.mappings = recipe.mappings || [];
      recipe.code = recipe.code || null;
      recipe.category = recipe.category || i18next.t('GENERAL.NONE_VALUE');
      recipe.variantsCount = recipe.variantsCount || 0;

      return recipe;
    });

    return formattedRecipes;
  };

  const getCategoriesAndSubCategories = async () => {
    const { categories: fetchedCategories } = await clientService.getCategoriesAndSubCategories(
      isInCentralKitchenOnlyPage
        ? CATEGORY_TYPES_OBJECT.CENTRAL_KITCHEN_RECIPE
        : CATEGORY_TYPES_OBJECT.RECIPE,
    );

    const formattedCategories = fetchedCategories.map((category) => ({
      ...category,
      name: category.name || i18next.t('GENERAL.NONE_VALUE'),
      value: category.name || i18next.t('GENERAL.NONE_VALUE'),
    }));

    setCategories(formattedCategories);

    return formattedCategories;
  };

  const formatRecipesUnits = (recipes) => {
    const formattedRecipes = recipes.map((recipe) => ({
      ...recipe,
      unit: translateUnit(recipe.unit),
    }));

    return formattedRecipes;
  };

  const createRecipe = () => {
    const path = getIsInCentralKitchenOnlyPage()
      ? '/admin/kitchen-products/recipes/create'
      : '/admin/products/recipes/create';

    history.push(path);
  };

  const duplicateRecipe = (recipes) => {
    const path = getIsInCentralKitchenOnlyPage()
      ? '/admin/kitchen-products/recipes/create'
      : '/admin/products/recipes/create';

    history.push({
      pathname: path,
      recipe: recipes[0],
      type: 'duplicate',
    });
  };

  const activateMultipleChannels = (selectedRecipes) => {
    const handleAction = async () => {
      pageLoading();

      try {
        const recipeIds = selectedRecipes.map(({ id }) => id);

        await recipeService.activateMulitpleChannelsByIds(recipeIds);

        showSuccessMessage(i18next.t('ADMIN.RECIPES.ACTIVATE_MULTIPLE_CHANNELS_ACTION_SUCCESS'));

        await getRecipes();
      } catch {
        showErrorMessage(i18next.t('ADMIN.RECIPES.ACTIVATE_MULTIPLE_CHANNELS_ACTION_FAILURE'));
      }

      pageLoaded();
    };

    const actions = [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        key: 0,
        color: 'inpulse-default',
        handleClick: handleAction,
        label: i18next.t('GENERAL.PRE_FILL'),
        icon: '/images/inpulse/bolt-white-small.svg',
      },
    ];

    const params = getConfirmationModal({
      title: i18next.t('ADMIN.RECIPES.ACTIVATE_MULTIPLE_CHANNELS_MODAL_TITLE'),
      icon: '/images/inpulse/bolt-white-small.svg',
      actions,
      content: i18next.t('ADMIN.RECIPES.ACTIVATE_MULTIPLE_CHANNELS_MODAL_CONTENT'),
    });

    openGenericModal(params);
  };

  const getBrandsOfClient = () => {
    if (!hasMultipleBrands) {
      return;
    }

    if (!!contextHandler && !isEmpty(selectedBrands)) {
      return;
    }

    if (setSelectedBrands) {
      setSelectedBrands(clientBrands);
    }
  };

  const getRecipes = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const columnPropertyKeys = columns.map((column) => column.propertyKey);

      const { search, skip, limit, orderBy, orderType } = paginationUtils.getPayloadPagination({
        columnPropertyKeys,
        listViewQueryParams,
      });

      // Convert skip back to page for the API (API expects page starting from 1)
      const page = Math.floor(skip / limit) + 1;

      const { data, count } = await recipeService.getPaginated(
        {
          orderBy,
          orderType,
          page,
          limit,
          search: search || '',
        },
        {
          ...(hasMultipleBrands && {
            brandIds: {
              inArray: (selectedBrands || []).map(({ id }) => id),
            },
          }),
          active: { eq: get(selectedStatus, 'filterValue', false) },
          quantity: customParams.quantity,
          categoryId: customParams.categoryId,
          parentsCount: customParams.parentsCount,
          mappingsCount: customParams.mappingsCount,
          variantsCount: customParams.variantsCount,
          unit: customParams.unit,
          isKitchen: { eq: isInCentralKitchenOnlyPage },
          selectedBrands,
        },
      );

      if (!data || !data.length) {
        setRecipes([]);

        setTotalRecipes(0);

        if (isInitialFetch) {
          setEmptyState(true);
        }

        return;
      }

      const formattedRecipes = formatRecipesUnits(preprocessRecipes(data));

      setRecipes(formattedRecipes);
      setTotalRecipes(count);
    } catch {
      showErrorMessage(i18next.t('GENERAL.FETCH_CLIENT_PRODUCTS_FAILURE'));

      setRecipes([]);
      setTotalRecipes(0);
    } finally {
      setIsLoading(false);
      setIsInitialFetch(false);
    }
  };

  const updateColumnsFilterList = (recipes, columns) => {
    const updatedColumnsList = columns.reduce((result, column) => {
      if (!column.filterType) {
        return result;
      }

      const item = { ...column };

      if (column.filterType === 'string' && !['categoryId', 'unit'].includes(column.propertyKey)) {
        const choices = recipes.reduce((list, recipe, key) => {
          if (recipe[column.propertyKey]) {
            if (typeof recipe[column.propertyKey] === 'object') {
              recipe[column.propertyKey].forEach((elem, index) => {
                if (!list[elem]) {
                  list[elem] = {
                    id: index,
                    name: elem,
                    [column.propertyKey]: elem,
                  };
                }
              });
            } else {
              if (!list[recipe[column.propertyKey]]) {
                list[recipe[column.propertyKey]] = {
                  id: key,
                  name: recipe[column.propertyKey],
                  [column.propertyKey]: recipe[column.propertyKey],
                };
              }
            }
          }
          return list;
        }, {});

        if (!Object.keys(choices).length) {
          return result;
        }

        item.list = sortArrayOfObjectsAlphabetically(Object.values(choices), 'name');
      }

      if (column.filterType === 'string' && column.propertyKey === 'categoryId') {
        const choices = categories.reduce((acc, category, index) => {
          acc.push({
            id: index,
            name: category.name,
            categoryId: category.id,
          });

          return acc;
        }, []);

        if (isEmpty(choices)) {
          return result;
        }

        item.list = choices;
      }

      if (column.filterType === 'string' && column.propertyKey === 'unit') {
        const choices = [
          {
            id: 0,
            name: i18next.t('GENERAL.UNIT'),
            unit: ENTITY_UNITS.UNIT,
          },
          {
            id: 1,
            name: ENTITY_UNITS.GRAM,
            unit: ENTITY_UNITS.GRAM,
          },
          {
            id: 2,
            name: ENTITY_UNITS.MILLILITER,
            unit: ENTITY_UNITS.MILLILITER,
          },
        ];

        item.list = choices;
      }

      result.push(item);

      return result;
    }, []);

    setColumnsFilterList(updatedColumnsList);
  };

  const disableRecipes = async (recipes) => {
    const recipeIds = recipes.map((recipe) => recipe.id);

    pageLoading();

    try {
      await recipeService.disableRecipes(recipeIds);

      await getRecipes();

      showSuccessMessage(
        recipeIds.length === 1
          ? i18next.t('ADMIN.RECIPES.SINGLE_DISABLE_SUCCESS')
          : i18next.t('ADMIN.RECIPES.MULTIPLE_DISABLE_SUCCESS'),
      );
    } catch (error) {
      showErrorMessage(
        recipeIds.length === 1
          ? i18next.t('ADMIN.RECIPES.SINGLE_DISABLE_ERROR')
          : i18next.t('ADMIN.RECIPES.MULTIPLE_DISABLE_ERROR'),
      );
    } finally {
      pageLoaded();
    }
  };

  const enableRecipe = async (recipeToEnable) => {
    const recipeToEnableId = recipeToEnable.id;

    pageLoading();

    try {
      await recipeService.enableRecipe(recipeToEnableId);

      await getRecipes();

      showSuccessMessage(i18next.t('ADMIN.RECIPES.ENABLE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.RECIPES.ENABLE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleDeletionWarningGenericModal = (recipesToDelete) => {
    const modalConfig = getDeleteRecipesModalParams(recipesToDelete, deleteDisabledRecipes);

    openGenericModal(modalConfig);
  };

  const deleteDisabledRecipes = async (recipes) => {
    const recipeIds = recipes.map((recipe) => recipe.id);

    pageLoading();
    try {
      await recipeService.deleteDisabledRecipes(recipeIds);

      const message =
        recipeIds.length === 1
          ? i18next.t('ADMIN.RECIPES.DELETE')
          : i18next.t('ADMIN.RECIPES.DELETE_PLURAL');

      await getRecipes();
      showSuccessMessage(message);
    } catch {
      const message =
        recipeIds.length === 1
          ? i18next.t('ADMIN.RECIPES.SINGLE_DELETE_ERROR')
          : i18next.t('ADMIN.RECIPES.MULTIPLE_DELETE_ERROR');

      showErrorMessage(message);
    } finally {
      pageLoaded();
    }
  };

  const triggerExport = async () => {
    const recipesIds = selectedRecipes.length ? selectedRecipes.map(({ id }) => id) : [];

    openModalExportInfo({
      component: ExportRecipeModal,
      title: i18next.t('ADMIN.RECIPES.EXPORT'),
      recipesIds,
      customParams: recipesIds.length > 0 ? null : customParams, // Only pass filters if no selection
      selectedStatus: recipesIds.length > 0 ? null : selectedStatus, // Only pass status if no selection
      isInCentralKitchenOnlyPage,
    });
  };

  const handleExportByStoreCloseCleanUp = () => {
    setIsExportByStoreModalOpen(false);
    setExportByStoreGroups([]);
    setExportByStoreSelectedGroups([]);
    setExportByStoreStores([]);
    setExportByStoreSelectedStores([]);
  };

  const handleExportByStore = () => {
    const recipesIds = selectedRecipes.length ? selectedRecipes.map(({ id }) => id) : [];

    handleExportByStoreCloseCleanUp();
    closeGenericModal();

    openModalExportInfo({
      component: ExportRecipeByStoreProgressModal,
      title: i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE_MODAL_TITLE'),
      selectedStores: exportByStoreSelectedStores,
      recipesIds,
      customParams: recipesIds.length > 0 ? null : customParams,
      selectedStatus: recipesIds.length > 0 ? null : selectedStatus,
      selectedBrands,
      isInCentralKitchenOnlyPage,
    });
  };

  const triggerExportByStore = () => {
    const allGroups = getSelectableGroupsFromStores(salesPointStores);
    const initialStores = allGroups.length
      ? getFilteredStoresWithSelectedGroups(allGroups, salesPointStores)
      : salesPointStores;

    setExportByStoreGroups(allGroups);
    setExportByStoreSelectedGroups(allGroups);
    setExportByStoreStores(initialStores);
    setExportByStoreSelectedStores(initialStores);
    setIsExportByStoreModalOpen(true);

    openGenericModal(
      getExportByStoreModalConfig({
        groups: allGroups,
        selectedGroups: allGroups,
        setSelectedGroups: setExportByStoreSelectedGroups,
        stores: initialStores,
        selectedStores: initialStores,
        setSelectedStores: setExportByStoreSelectedStores,
        handleExport: handleExportByStore,
        handleCloseCleanUp: handleExportByStoreCloseCleanUp,
      }),
    );
  };

  const handleAvailabilitiesUpdate = async (recipes, availabilities) => {
    pageLoading();

    try {
      const recipeIds = recipes.map(({ id }) => id);

      await recipeService.updateRecipesAvailabilities(
        recipeIds,
        availabilities.hasStock,
        availabilities.hasLoss,
        availabilities.hasTransfer,
      );

      await getRecipes();

      showSuccessMessage(i18next.t('ADMIN.RECIPES.AVAILABILITIES_SUCCESSFULLY_UPDATED'));
      handleAvailabilitiesModalCloseCleanUp();
    } catch {
      showErrorMessage(i18next.t('ADMIN.RECIPES.AVAILABILITIES_UPDATE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleAvailabilitiesModalCloseCleanUp = () => {
    setSelectedAvailabilities({
      hasStock: true,
      hasLoss: true,
      hasTransfer: true,
    });

    closeGenericModal();
  };

  const openAvailabilitiesUpdateModal = async (selectedItems) => {
    setSelectedRecipes(selectedItems);

    const params = getAvailabilitiesModalConfig({
      handleAvailabilitiesUpdate,
      selectedItems,
      selectedAvailabilities,
      setSelectedAvailabilities,
      handleAvailabilitiesModalCloseCleanUp,
    });

    openGenericModal(params);
  };

  const renderEmptyState = () => (
    <GeneralEmptyStateListView
      icon={'/images/inpulse/recipe.svg'}
      renderAction={() => (
        <Button
          color="inpulse-default"
          handleClick={createRecipe}
          icon={'/images/inpulse/add-white-small.svg'}
          label={i18next.t('ADMIN.RECIPES.CREATE')}
        />
      )}
      subtitle={i18next.t('ADMIN.RECIPES.SUBTITLE')}
      title={i18next.t('ADMIN.RECIPES.TITLE')}
    />
  );

  const getCustomMultipleDropdowns = () => {
    if (!hasMultipleBrands || !clientBrands) {
      return [];
    }

    return [
      {
        id: 'brands-list',
        list: clientBrands,
        selectedItems: selectedBrands,
        setSelectedItems: setSelectedBrands,
        defaultSelectedItems: selectedBrands,
        icon: '/images/inpulse/brand-black-small.svg',
      },
    ];
  };

  const categoriesBatchUpdateModal = useCategoriesBatchUpdateModal({
    onRefresh: getRecipes,
    loadProps: {
      clientId,
      type: isInCentralKitchenOnlyPage
        ? CATEGORY_TYPES_OBJECT.CENTRAL_KITCHEN_RECIPE
        : CATEGORY_TYPES_OBJECT.RECIPE,
      propertyType: 'categories',
    },
    itemListSorterParams: {
      disableMergeItems: true,
      disableCreateItem: false,
      entityName: i18next.t('FEATURE.ADMIN.RECIPES'),
      createItemLabel: i18next.t('ADMIN.GENERAL.MANAGE_CATEGORIES_CREATE_LABEL'),
    },
  });

  return (
    <>
      <NavigationBreadCrumb featurePath={path} />
      {emptyState ? (
        renderEmptyState()
      ) : (
        <ListViewContainer>
          <ListView
            {...paginationProps}
            actionOnClick={goToRecipeDetailsPage}
            actions={actions}
            columns={columns}
            data={recipes}
            defaultSearchInput={search}
            isLoading={isLoading}
            languageCode={userLanguageCode}
            minActionsInActionsDropdown={1}
            padding={STANDARD_LISTVIEW_PADDING}
            placeholderShape={i18next.t('GENERAL.SEARCH')}
            renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
            renderFilterButton={() => (
              <DeepsightFiltersButton
                advancedFilters={advancedFilters}
                applyFilters={applyFilters}
                columnsFilterList={columnsFilterList}
                customMultipleDropDowns={getCustomMultipleDropdowns()}
                customSingleDropDowns={[
                  {
                    id: 'recipe-state',
                    isDisabled: isUserManager(user) && !isUserDeepsight(user),
                    disabledIcon: '/images/inpulse/power-ip-black.svg',
                    itemSelectedIcon: '/images/inpulse/power-ip-black.svg',
                    list: CHOICES_DROPDOWN_ACTIVE(),
                    defaultSelectedItem: CHOICES_DROPDOWN_ACTIVE()[0],
                    selectedItem: selectedStatus,
                    setSelectedItem: setSelectedStatus,
                  },
                ]}
                filters={filters}
                isLoading={isLoading}
                readOnly={isLoading}
                setAdvancedFilters={setAdvancedFilters}
                setApplyFilters={setApplyFilters}
                setFilters={setFilters}
                textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
              />
            )}
            rowActions={rowActions}
            setSelectedItems={setSelectedRecipes}
          />
        </ListViewContainer>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  recipes: state.baseReducer.recipes,
  client: getClientInfo(state.baseReducer.user),
  isCentralView: state.baseReducer.isCentralView,
  clientBrands: state.baseReducer.brands,
  hasVariants: getHasRecipeVariants(state.baseReducer.userRights),
  activeStores: state.baseReducer.activeStores,
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Recipes);
